<template>
<div>
    <AppBar />

    <!-- HERO SECTION -->
    <section class="what-hero">
        <div class="hero-overlay"></div>
        <v-container class="hero-container">
            <div class="hero-card">
                <!-- LEFT TEXT -->
                <div class="hero-text">
                    <h1 class="hero-title">{{ $t("WhatWeDoHeroTitle") }}</h1>
                    <p class="hero-subtitle">{{ $t("WhatWeDoHeroSubtitle") }}</p>
                </div>

                <!-- RIGHT IMAGE -->
                <div class="hero-image-wrapper">
                    <div class="hero-image-shape">
                        <img :src="heroImage" alt="Agriculture" loading="lazy" />
                    </div>
                </div>
            </div>
        </v-container>
    </section>

    <!-- WHAT WE DO SECTION -->
    <section class="content-section fade-in-up">
        <v-container>
            <div class="text-center mb-12">
                <h2 class="section-title mb-2">{{ $t("OurServices") }}</h2>
                <div class="section-underline"></div>
                <p class="section-subtitle">{{ $t("OurServicesSubtitle") }}</p>
            </div>

            <v-row justify="center" class="gap-6 mt-12">
                <v-col v-for="(item, index) in whatWeDoPoints" :key="index" cols="12" sm="6" md="4" lg="3">
                    <div class="service-card" @click="toggleExpand(index)">
                        <div class="icon-wrapper">
                            <v-icon size="50" color="green darken-2">{{ item.icon }}</v-icon>
                        </div>

                        <h3 class="service-title">{{ $t(item.titleKey) }}</h3>
                        <p class="service-description">{{ $t(item.descriptionKey) }}</p>

                        <v-expand-transition>
                            <div v-if="expandedIndex === index" class="service-more">
                                <p>{{ $t(item.moreKey) }}</p>
                            </div>
                        </v-expand-transition>

                        <p class="learn-more-link">
                            {{ expandedIndex === index ? $t("ShowLess") : $t("LearnMore") }}
                            <v-icon size="18" class="ml-1">
                                {{ expandedIndex === index ? "mdi-chevron-up" : "mdi-chevron-down" }}
                            </v-icon>
                        </p>
                    </div>
                </v-col>
            </v-row>
        </v-container>
    </section>

    <!-- TARGET MARKET SECTION -->
    <section class="target-market-section py-20 my-8">
        <v-container>
            <h2 class="section-title text-center mb-12">{{ $t("TargetMarketTitle") }}</h2>
            <div class="section-underline"></div>
            <p class="section-subtitle text-center mb-12">{{ $t("TargetMarketSubtitle") }}</p>

            <div class="market-strips">
                <div v-for="(market, index) in targetMarkets" :key="index" class="market-strip" :style="{ backgroundColor: index % 2 === 0 ? '#2e7d32' : '#b87c4c' }">
                    <div class="strip-icon">
                        <v-icon size="48" color="white">{{ market.icon }}</v-icon>
                    </div>
                    <div class="strip-text">
                        <h3 class="strip-title">{{ $t(market.titleKey) }}</h3>
                        <p class="strip-description">{{ $t(market.descriptionKey) }}</p>
                    </div>
                </div>
            </div>
        </v-container>
    </section>

    <!-- OUR PROCESS SECTION -->
    <section class="process-section">
        <v-container>
            <div class="text-center mb-10">
                <h2 class="section-title">{{ $t("OurProcess") }}</h2>
                <div class="section-underline"></div>
                <p class="section-subtitle">{{ $t("OurProcessSubtitle") }}</p>
            </div>

            <div class="horizontal-timeline">
                <div v-for="(step, index) in processSteps" :key="index" class="timeline-step">
                    <div class="timeline-number">{{ index + 1 }}</div>
                    <div v-if="index < processSteps.length - 1" class="timeline-connector"></div>

                    <div class="timeline-card">
                        <div class="timeline-icon">
                            <v-icon size="36" color="#2e7d32">{{ step.icon }}</v-icon>
                        </div>
                        <h3 class="timeline-title">{{ $t(step.titleKey) }}</h3>
                        <p class="timeline-desc">{{ $t(step.descriptionKey) }}</p>
                    </div>
                </div>
            </div>
        </v-container>
    </section>

    <Footer />
</div>
</template>

<script>
import Footer from "../Layout/Footer";
import AppBar from "../Layout/appBar";
import HeroImg from "@/assets/beans.jpg";

export default {
    name: "WhatWeDo",
    components: {
        Footer,
        AppBar
    },
    data() {
        return {
            heroImage: HeroImg,
            expandedIndex: null,

            whatWeDoPoints: [{
                    titleKey: "WhatWeDo.CultivationProduction.title",
                    descriptionKey: "WhatWeDo.CultivationProduction.description",
                    moreKey: "WhatWeDo.CultivationProduction.more",
                    icon: "mdi-sprout",
                },
                {
                    titleKey: "WhatWeDo.Procurement.title",
                    descriptionKey: "WhatWeDo.Procurement.description",
                    moreKey: "WhatWeDo.Procurement.more",
                    icon: "mdi-basket",
                },
                {
                    titleKey: "WhatWeDo.FoodProcessing.title",
                    descriptionKey: "WhatWeDo.FoodProcessing.description",
                    moreKey: "WhatWeDo.FoodProcessing.more",
                    icon: "mdi-food-variant",
                },
                {
                    titleKey: "WhatWeDo.FarmerTraining.title",
                    descriptionKey: "WhatWeDo.FarmerTraining.description",
                    moreKey: "WhatWeDo.FarmerTraining.more",
                    icon: "mdi-school",
                },
                {
                    titleKey: "WhatWeDo.Research.title",
                    descriptionKey: "WhatWeDo.Research.description",
                    moreKey: "WhatWeDo.Research.more",
                    icon: "mdi-magnify",
                },
                {
                    titleKey: "WhatWeDo.Sales.title",
                    descriptionKey: "WhatWeDo.Sales.description",
                    moreKey: "WhatWeDo.Sales.more",
                    icon: "mdi-truck-delivery",
                },
                {
                    titleKey: "WhatWeDo.Partnership.title",
                    descriptionKey: "WhatWeDo.Partnership.description",
                    moreKey: "WhatWeDo.Partnership.more",
                    icon: "mdi-handshake",
                },
            ],

            processSteps: [{
                    titleKey: "ProcessSteps.Consultation.title",
                    descriptionKey: "ProcessSteps.Consultation.description",
                    icon: "mdi-account-tie",
                },
                {
                    titleKey: "ProcessSteps.ProcessingPackaging.title",
                    descriptionKey: "ProcessSteps.ProcessingPackaging.description",
                    icon: "mdi-package-variant-closed",
                },
                {
                    titleKey: "ProcessSteps.Marketing.title",
                    descriptionKey: "ProcessSteps.Marketing.description",
                    icon: "mdi-bullhorn",
                },
                {
                    titleKey: "ProcessSteps.ExportLogistics.title",
                    descriptionKey: "ProcessSteps.ExportLogistics.description",
                    icon: "mdi-airplane",
                },
            ],

            targetMarkets: [{
                    titleKey: "TargetMarkets.LocalFarmers.title",
                    descriptionKey: "TargetMarkets.LocalFarmers.description",
                    icon: "mdi-account-multiple",
                },
                {
                    titleKey: "TargetMarkets.AgriculturalCooperatives.title",
                    descriptionKey: "TargetMarkets.AgriculturalCooperatives.description",
                    icon: "mdi-home-group",
                },
                {
                    titleKey: "TargetMarkets.AgribusinessCompanies.title",
                    descriptionKey: "TargetMarkets.AgribusinessCompanies.description",
                    icon: "mdi-factory",
                },
                {
                    titleKey: "TargetMarkets.InternationalBuyers.title",
                    descriptionKey: "TargetMarkets.InternationalBuyers.description",
                    icon: "mdi-earth",
                },
            ],
        };
    },
    methods: {
        setInitialCardHeights() {
            const cards = this.$el.querySelectorAll('.service-card');
            let maxHeight = 0;

            cards.forEach(card => {
                const cardHeight = card.offsetHeight;
                if (cardHeight > maxHeight) maxHeight = cardHeight;
            });

            cards.forEach(card => {
                card.style.minHeight = maxHeight + 'px'; // equal height
            });
        },
        toggleExpand(index) {
            this.expandedIndex = this.expandedIndex === index ? null : index;
        },
    },
    mounted() {
        this.$nextTick(() => {
            this.setInitialCardHeights();
        });
    },
};
</script>

<style scoped>
/* HERO SECTION */
/* HERO SECTION */
.what-hero {
    position: relative;
    background: url("@/assets/choroko.jpg") center/cover no-repeat;
    min-height: 70vh;
    display: flex;
    align-items: center;
    padding: 5rem 1rem;
    overflow: hidden;
}

.hero-overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 1;
}

.hero-container {
    position: relative;
    z-index: 2;
}

.hero-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 2rem;
    background: #2e7d32;
    border-radius: 28px;
    padding: 3rem;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.35);
    flex-wrap: wrap;
}

/* LEFT TEXT */
.hero-text {
    flex: 1;
    color: #fff;
}

.hero-title {
    font-size: 3rem;
    font-weight: 900;
    margin-bottom: 1.5rem;
}

.hero-subtitle {
    font-size: 1.2rem;
    line-height: 1.7;
    opacity: 0.95;
    margin-bottom: 2rem;
    max-width: 500px;
    font-style: italic;
}

.hero-btn {
    background: #fff;
    color: #2e7d32;
    font-weight: 700;
    padding: 0.8rem 2rem;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
}

.hero-btn:hover {
    background: #f1f8f6;
    transform: translateY(-3px);
}

/* RIGHT IMAGE SHAPE */
.hero-image-wrapper {
    flex: 0 0 auto;
    display: flex;
    justify-content: center;
    align-items: center;
}

.hero-image-shape {
    width: 220px;
    height: 220px;
    border-radius: 50%;
    overflow: hidden;
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.25);
    border: 6px solid rgba(255, 255, 255, 0.8);
    transition: transform 0.4s ease;
}

.hero-image-shape:hover {
    transform: scale(1.05);
}

.hero-image-shape img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

/* RESPONSIVE */
@media (max-width: 960px) {
    .hero-card {
        flex-direction: column;
        text-align: center;
        padding: 2rem;
    }

    .hero-title {
        font-size: 2.2rem;
    }

    .hero-subtitle {
        font-size: 1rem;
        max-width: 100%;
    }

    .hero-image-ellipse {
        width: 220px;
        height: 220px;
        margin-top: 1.5rem;
    }
}

/* WHAT WE DO CARDS */
.service-card {
    background: linear-gradient(135deg, #f8f8f8 0%, #edf7f6 50%, #f2fffd 100%);
    border-radius: 20px;
    padding: 2rem 1.5rem;
    text-align: center;
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.08);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    transition: all 0.3s ease;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
}

.service-card:hover {
    transform: translateY(-6px);
    box-shadow: 0 20px 50px rgba(46, 125, 50, 0.12);
    /* subtle brand-colored shadow */
}

.icon-wrapper {
    margin-bottom: 1rem;
    width: 70px;
    height: 70px;
    border-radius: 50%;
    background: linear-gradient(135deg, #e0f2f1, #c8e6c9);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1rem;
}

.service-title {
    font-size: 1.2rem;
    font-weight: 700;
    color: #2e7d32;
    margin-bottom: 0.5rem;
}

.service-description {
    font-size: 0.95rem;
    color: #555;
    line-height: 1.5;
}

.service-more {
    margin-top: 1rem;
    color: #333;
    font-size: 0.9rem;
    line-height: 1.4;
}

.learn-more-link {
    margin-top: auto;
    font-weight: 600;
    color: #2e7d32;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: color 0.3s ease;
}

.learn-more-link:hover {
    color: #1b5e20;
}

/* Target Market Horizontal Strips */

.market-strips {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

/* Strip style */
.market-strip {
    display: flex;
    align-items: center;
    gap: 2rem;
    padding: 1rem 2.5rem;
    border-radius: 16px;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    cursor: default;
    color: white;
}

.market-strip:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.15);
}

/* Icon */
.strip-icon {
    flex-shrink: 0;
    width: 70px;
    height: 70px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
}

/* Text */
.strip-text {
    flex: 1;
}

.strip-title {
    font-size: 1.4rem;
    font-weight: 700;
    margin-bottom: 0.4rem;
}

.strip-description {
    font-size: 1rem;
    line-height: 1.5;
}

/* Background Colors */
.green-bg {
    background: #2e7d32;
}

.brown-bg {
    background: #b87c4c;
}

/* Responsive */
@media (max-width: 960px) {
    .market-strip {
        flex-direction: column;
        text-align: center;
        gap: 1rem;
    }
}

/* HORIZONTAL TIMELINE DESIGN */
.process-section {
    padding: 6rem 0;
}

.horizontal-timeline {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    position: relative;
    margin-top: 4rem;
    gap: 2rem;
    flex-wrap: wrap;
}

/* Connecting line */
.horizontal-timeline::before {
    content: "";
    position: absolute;
    top: 35px;
    left: 50%;
    transform: translateX(-50%);
    width: 85%;
    height: 6px;
    background: linear-gradient(to right, #2e7d32, #b87c4c);
    border-radius: 3px;
    z-index: 0;
}

/* Each Step */
.timeline-step {
    position: relative;
    flex: 1;
    min-width: 200px;
    text-align: center;
    z-index: 2;
}

/* Circle Number */
.timeline-number {
    width: 70px;
    height: 70px;
    background: linear-gradient(135deg, #2e7d32, #b87c4c);
    color: #fff;
    font-weight: 800;
    font-size: 1.5rem;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 auto;
    position: relative;
    z-index: 2;
    box-shadow: 0 12px 30px rgba(46, 125, 50, 0.35);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.timeline-step:hover .timeline-number {
    transform: scale(1.1);
    box-shadow: 0 20px 45px rgba(46, 125, 50, 0.4);
}

/* Connector line between circles */
.timeline-connector {
    position: absolute;
    top: 35px;
    left: 50%;
    width: 100%;
    height: 6px;
    background: linear-gradient(to right, #2e7d32, #b87c4c);
    z-index: 1;
    transform: translateX(35px);
}

/* Card Below the Circle */
.timeline-card {
    background: #fff;
    border-radius: 20px;
    padding: 2rem 1.5rem 1.5rem;
    box-shadow: 0 15px 35px rgba(46, 125, 50, 0.12);
    margin-top: 3.5rem;
    transition: all 0.3s ease;
    height: 100%;
    /* fill the column height */
    min-height: 300px;
    /* optional, ensures consistency */
}

.timeline-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 25px 55px rgba(46, 125, 50, 0.25);
}

/* Icon */
.timeline-icon {
    width: 65px;
    height: 65px;
    border-radius: 50%;
    background: rgba(46, 125, 50, 0.08);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1rem;
}

/* Titles & Description */
.timeline-title {
    font-weight: 700;
    color: #2e7d32;
    margin-bottom: 0.4rem;
    font-size: 1.1rem;
}

.timeline-desc {
    font-size: 0.95rem;
    color: #555;
    line-height: 1.6;
}

/* Responsive: stack vertically */
@media (max-width: 960px) {
    .horizontal-timeline {
        flex-direction: column;
        align-items: center;
    }

    .horizontal-timeline::before {
        display: none;
    }

    .timeline-step {
        width: 80%;
        text-align: center;
    }

    .timeline-connector {
        display: none;
    }

    .timeline-card {
        margin-top: 2.5rem;
    }
}
</style>
